import React, { useEffect, useState, useRef, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import useLoggedUser from "../../../hooks/useLoggedUser";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import {
  IUserProfile,
  RefrenceEntity,
  RefrenceType,
} from "../models/UserProfile";
import { UserProfileService } from "../service/userProfileService";
import ProfilePictureController from "../../../components/organisms/Cards/user-card-controller/ProfilePictureController";
import { useMsal } from "@azure/msal-react";
import Loader from "../../../components/atoms/Loader/Loader";
import { Link, useLocation } from "react-router-dom";
import IconHome from "../../../styles/icons/IconHome";
import ActionModal from "../../../components/molecules/modal/ActionModal";
import { TodosContext } from "../../../context";
import Headline from "../../../components/organisms/Headline/Headline";
import OrganisationDeactivatedNotice from "../../../components/molecules/OrganisationDeactivatedNotice/OrganisationDeactivatedNotice";
import { useOrganisationStatus } from "../../../context/organisationStatus";

export default function UserProfile(): JSX.Element {
  const loggedUser = useLoggedUser();
  const { addAlert } = useContext(TodosContext);
  const { isOrganisationActive } = useOrganisationStatus();
  const userId = loggedUser?.userId || "";
  const fileInputRef = useRef<HTMLInputElement>(null);
  const userProfileService = new UserProfileService();
  let location = useLocation();
  const { instance, inProgress } = useMsal();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [profilePicture, setProfilePicture] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isConfirmationModalShow, setIsConfirmationModalShow] =
    React.useState(false);
  const handleOpenModal = () => setIsConfirmationModalShow(true);
  const handleCloseModal = () => setIsConfirmationModalShow(false);
  const [form, setForm] = useState<IUserProfile>({
    FirstName: "",
    LastName: "",
    Email: "",
    CompanyName: "",
    JobTitle: "",
    MobileNumber: "",
    Type: "",
  });
  const [settings, setSettings] = useState({
    isFeedbackSms: false,
    isFeedbackEmail: false,
    isLeadsSms: false,
    isLeadsEmail: false,
  });
  useEffect(() => {
    setLoading(true);
    setIsLoadingProfile(true);
    if (!loggedUser?.userId) return;
    const playload = {
      Reference: RefrenceEntity.Users,
      Type: RefrenceType.ProfilePicture,
      ReferenceId: Number(loggedUser.userId),
    };
    userProfileService
      .fetchUserProfilePicture(playload)
      .then((data) => {
        if (data.succeeded) {
          const base64Data = data.data.content;
          const contentType = data.data.contentType ?? "image/png";
          const imageUrl = `data:${contentType};base64,${base64Data}`;
          setProfilePicture(imageUrl);
          setIsLoadingProfile(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching profile picture:", err);
      })
      .catch((err) => {
        if (err.message.includes("404")) {
          setProfilePicture("");
        }
        throw err;
      })
      .finally(() => {
        setIsLoadingProfile(false);
      });
    userProfileService
      .fetchCurrentUser()
      .then((data) => {
        setSettings({
          isFeedbackSms: data.userSettings?.isFeedbackSms ?? false,
          isFeedbackEmail: data.userSettings?.isFeedbackEmail ?? false,
          isLeadsSms: data.userSettings?.isLeadsSms ?? false,
          isLeadsEmail: data.userSettings?.isLeadsEmail ?? false,
        });
        setUser(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to load user");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userId]);

  const handleToggleChange = async (
    key: keyof typeof settings,
    type: number
  ) => {
    try {
      setLoading(true);
      const updatedValue = !settings[key];

      // Optimistic update (UI changes immediately)
      setSettings((prev) => ({
        ...prev,
        [key]: updatedValue,
      }));

      await userProfileService.updateUserSetting(Number(userId), type);
      await userProfileService
        .fetchCurrentUser()
        .then((data) => {
          setSettings({
            isFeedbackSms: data.userSettings?.isFeedbackSms ?? false,
            isFeedbackEmail: data.userSettings?.isFeedbackEmail ?? false,
            isLeadsSms: data.userSettings?.isLeadsSms ?? false,
            isLeadsEmail: data.userSettings?.isLeadsEmail ?? false,
          });
          setUser(data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message || "Failed to load current user");
        });

      addAlert("Setting updated successfully", "success", true);
    } catch (error) {
      // Revert if failed
      setSettings((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
      alert("Failed to update setting");
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB");
      return;
    }

    try {
      setUploadingImage(true);

      // Convert file to base64
      const toBase64 = (file: File) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () =>
            resolve((reader.result as string).split(",")[1]); // get Base64 part
          reader.onerror = (error) => reject(error);
        });

      const base64Content = await toBase64(file);

      const blobPayload = {
        Reference: RefrenceEntity.Users,
        Type: RefrenceType.ProfilePicture,
        ReferenceId: Number(userId),
        Data: {
          Uri: "", // backend might generate this
          Name: file.name,
          ContentType: file.type,
          Content: base64Content,
        },
      };

      const response = await userProfileService.uploadUserProfilePicture(
        blobPayload,
        file
      );
      const playload = {
        Reference: RefrenceEntity.Users,
        Type: RefrenceType.ProfilePicture,
        ReferenceId: Number(userId),
      };
      userProfileService
        .fetchUserProfilePicture(playload)
        .then((data) => {
          if (data.succeeded) {
            const base64Data = data.data.content;
            const contentType = data.data.contentType ?? "image/png";
            const imageUrl = `data:${contentType};base64,${base64Data}`;
            setProfilePicture(imageUrl);
          }
        })
        .catch((err) => {
          console.error("Error fetching profile picture:", err);
        })
        .catch((err) => {
          if (err.message.includes("404")) {
            setProfilePicture("");
          }
          throw err;
        })
        .finally(() => {
          setIsLoadingProfile(false);
        });

      // Show preview
      const previewUrl = URL.createObjectURL(file);
      setProfilePicture(previewUrl);
      handleOpenModal();
    } catch (err: any) {
      console.error("Error uploading profile picture:", err);
      alert(err.message || "Failed to upload profile picture");
      handleCloseModal();
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleLogout = () => {
    // Clear tokens and redirect to login
    localStorage.clear();
    instance.logoutRedirect();
  };

  return (
    <div className="user-profile">
      <BreadCrumb />
      <Headline
        name="My Profile"
        subHeading="Manage your personal information and account settings"
      />
      {!isOrganisationActive && <OrganisationDeactivatedNotice />}
      <div className="user-profile-container">
        {isLoadingProfile ? (
          <Loader />
        ) : (
          <div className="profile-info box-container">
            <div
              className="profile-image-wrapper"
              onClick={handleProfilePictureClick}
            >
              <div className="profile-image">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile" />
                ) : (
                  <ProfilePictureController
                    firstName={user?.firstName || ""}
                    lastName={user?.lastName || ""}
                    image={profilePicture}
                  />
                )}
                <div className="profile-image-overlay">
                  <FontAwesomeIcon icon={faCamera} />
                  <span>
                    {uploadingImage ? "Uploading..." : "Change Photo"}
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </div>
            </div>
            <div>
              <h5>{user?.firstName + " " + user?.lastName}</h5>
              <p>{user?.type}</p>
            </div>
          </div>
        )}

        <div className="profile-details  box-container">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="title">
                <Headline
                  name="Personal Information"
                  subHeading="Keep your personal information accurate and up to date"
                />
                <div className="edit-button-wrapper">
                  <Link to={`${location.pathname}/edit/${user?.id}`}>
                    <div onClick={handleOpenModal}>
                      <IconHome breadcrumb="edit" activeColor={undefined} />
                    </div>
                  </Link>
                </div>
              </div>
              <div className="profile-information">
                <div className="profile-values">
                  <label>First Name</label>
                  <div>{user?.firstName}</div>
                </div>
                <div className="profile-values">
                  <label>Last Name</label>
                  <div>{user?.lastName}</div>
                </div>
                <div className="profile-values">
                  <label>Email</label>
                  <div>{user?.email}</div>
                </div>
                <div className="profile-values">
                  <label>Company Name</label>
                  <div>{user?.companyName}</div>
                </div>
                <div className="profile-values">
                  <label>Job Title</label>
                  <div>{user?.jobTitle}</div>
                </div>
                <div className="profile-values">
                  <label>Phone Number</label>
                  <div>{user?.mobileNumber}</div>
                </div>
                <div className="profile-values">
                  <label>Type</label>
                  <div>{user?.type}</div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="profile-setting box-container">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="title">
                <Headline
                  name="Settings"
                  subHeading="Manage your account settings and preferences"
                />
              </div>
              <div className="setting-container">
                <div className="setting-row">
                  <span>Feedback Notification (SMS)</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.isFeedbackSms}
                      onChange={(e) => handleToggleChange("isFeedbackSms", 1)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <span>Feedback Notification (Email)</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.isFeedbackEmail}
                      onChange={(e) => handleToggleChange("isFeedbackEmail", 2)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <span>Lead Rating Notification (SMS)</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.isLeadsSms}
                      onChange={(e) => handleToggleChange("isLeadsSms", 3)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="setting-row">
                  <span>Lead Rating Notification (Email)</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.isLeadsEmail}
                      onChange={(e) => handleToggleChange("isLeadsEmail", 4)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <ActionModal
        title={"Update Successfully"}
        show={isConfirmationModalShow}
        onClose={handleCloseModal}
        onConfirm={handleLogout}
      >
        <p>Now logout and login your account.</p>
      </ActionModal>
    </div>
  );
}
